import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard } from '../../../wizard/hooks/useWizard';
import { PipelineStorageKeys } from '../../constants/storage-keys';
import { CREATE_PIPELINE_STEP_GROUPS } from '../create-pipeline-state-machine/constants/create-pipeline-step-groups';
import { useCreatePipelineStateMachine } from '../create-pipeline-state-machine/hooks/useCreatePipelineStateMachine';
import { Integration } from '../../../shared/interfaces/integration';
import { resolveCreatePipelineSteps } from '../utils/createPipelineUtil';
import { WizardVersion } from '../../../wizard/interfaces/wizard-step';
import { getWizardCurrentStepInfo } from '../../../wizard/utils/wizardStepUtil';

export function useCreatePipelineWizard({
  integration,
  isCommonSourceTypeFlow
}: {
  integration: Integration;
  isCommonSourceTypeFlow: boolean;
}) {
  const navigate = useNavigate();

  const initialCache = useRef(
    new Map().set(PipelineStorageKeys.CREATE_PIPELINE, {
      steps: resolveCreatePipelineSteps(integration, isCommonSourceTypeFlow).steps,
      data: { integration },
      version: WizardVersion.V2
    })
  );

  const {
    controller,
    wizardState,
    createWizard,
    currentStep,
    stepTreeSnapshot,
    stepGroups,
    wizardCompleted,
    selectStepGroup,
    currentStepFooterProps,
    onCancelClick
  } = useWizard({
    initialCache: initialCache.current,
    onComplete: () => {},
    cancelUrl: '/pipeline?tab=edge',
    isCancelUrlPathRelative: true
  });

  const stateMachine = useCreatePipelineStateMachine();

  useEffect(() => {
    createWizard({
      stepsHeader: CREATE_PIPELINE_STEP_GROUPS,
      options: {
        trackingOptions: {
          storageKey: PipelineStorageKeys.CREATE_PIPELINE
        }
      },
      stateMachine
    });
  }, []);

  const onBackClick = () => {
    const { currentStepGroupIndex, currentStepIndex } = getWizardCurrentStepInfo(stepTreeSnapshot!);

    if (isCommonSourceTypeFlow && currentStepGroupIndex === 0 && currentStepIndex === 1) {
      navigate('/pipeline/create/select-source');
      return;
    }

    controller?.goBack();
  };

  return {
    stateMachine,
    wizardState,
    currentStep,
    stepGroups,
    stepTreeSnapshot,
    selectStepGroup,
    createWizard,
    onBackClick,
    onCancelClick,
    currentStepFooterProps,
    wizardCompleted
  };
}
